import React, {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../../providers/AuthProvider";
import {KpiInfo, KpiProps, ValidationError} from "../../../types/types";
import {getKpiInfo, getPreFilledKpiInfo, updateKpiInfo} from "../../../services/kpiService";
import {Loader} from "../../loader/Loader";
import {ErrorPage} from "../../error/ErrorPage";
import "./KpiSetup.css"
import {Button, Stepper, useMantineTheme} from "@mantine/core";
import {
    Clock,
    Code,
    Database,
    FileDescription, Filter, ListCheck,
} from "tabler-icons-react";
import {StepperContent} from "./stepper/content/StepperContent";
import KpiDefinitionStep from "./steps/definition/KpiDefinitionStep";
import {useMediaQuery} from "@mantine/hooks";
import {KpiTargetFilterConfigurationStep} from "./steps/filter/KpiTargetFilterConfigurationStep";
import {ScriptConfigurationStep} from "./steps/script/ScriptConfigurationStep";
import {SchedulerConfigurationStep} from "./steps/scheduler/SchedulerConfigurationStep";
import {SqlQueryConfigurationStep} from "./steps/query/sqlQueryConfigurationStep";
import {KpiDefinitionValidator} from "./steps/definition/kpiDefinitionValidator";
import {ReviewStep} from "./steps/review/ReviewStep";
import {SqlQueryConfigurationValidator} from "./steps/query/sqlQueryConfigurationValidator";
import {LoaderCard} from "../../loader/LoaderCard";
import {ScriptConfigurationValidator} from "./steps/script/ScriptConfigurationValidator";
import { SchedulerConfigurationValidator } from "./steps/scheduler/SchedulerConfigurationValidator";
import { ConfirmationPopUp } from "../../popup/confirmationPopUp";

export type KpiSetupState = {
    kpiInfo: KpiInfo,
    loading: boolean,
    error?: string,
    validationErrors: ValidationError[]
}

export function KpiSetup() {

    const [searchParams] = useSearchParams();

    const navigate = useNavigate();

    const [openSaveKpiConfirmationModal, setSaveKpiConfirmationModal] = useState(false);

    const [active, setActive] = useState(0);
    const [highestStepVisited, setHighestStepVisited] = useState(active);
    const [pushing, setPushing] = useState("not started");
    let theme = useMantineTheme();
    const authContext = localStorage.getItem("authContext")? JSON.parse(localStorage.getItem("authContext") ?? "{}"): {};
    const [state, setState] = useState<KpiSetupState>({
        loading: true,
        validationErrors: [],
        kpiInfo: {
            target: "USER",
            executionMode: "BATCH",
            extractorType: "SQL",
            batchSize: 1000,
            aggregationNames: [] as string[],
            extendedAttributes: {refreshMode:"SOFT",documentation:""},
            implementation: "com.applicate.channelkart.cli.kpi.services.GenericKpiExecutor",
            configuration: {queries: []} as object,
        } as KpiInfo
    });
    
    const nextStep = async () => {
        const validator = stepConfigurations[active].validator;
        if (validator) {
            const validationErrors = validator.validate(state.kpiInfo);
            if (validationErrors.length > 0) {
                setState({...state, validationErrors})
            } else {
                setState({...state, validationErrors: []})
                // good to go to the next page
                nextPage();
            }
        } else {
            nextPage();
        }
    };

    async function nextPage() {
        setHighestStepVisited((current) => (current < 5 ? current + 1 : current));
        setActive(current => current < 5 ? current + 1 : current);
    }

    const prevStep = () => {
        setHighestStepVisited((current) => (current > 0 ? current - 1 : current));
        setActive((current) => (current > 0 ? current - 1 : current));
    }

    useEffect(() => {
        const id = searchParams.get("id");
        const prefillId = searchParams.get("prefillId");
        if (id) {
            getKpiInfo(id, authContext).then(info => {
                setState({...state, loading: false, kpiInfo: info, error: undefined})
            }).catch(er => {
                console.log(er)
                setState({...state, loading: false, error: er.message})
            })
        }
        else if(prefillId){
            getKpiInfo(prefillId, authContext).then(info => {
                setState({...state, loading: false, kpiInfo: getPreFilledKpiInfo(info), error: undefined})
            }).catch(er => {
                console.log(er)
                setState({...state, loading: false, error: er.message})
            })
        } else {
            setState({...state, loading: false})
        }
        // eslint-disable-next-line
    }, []);

    const matches = useMediaQuery('(max-width: 990px)');

    if (state.loading) {
        return <Loader/>
    }

    if (state.error) {
        return <ErrorPage description={state.error}/>
    }

    const stepperIconProps = {
        size: 40,
        strokeWidth: 2,
        color: theme.colorScheme === 'dark' ? 'white' : "black"
    }

    const setKpiInfo = (kpiInfo: KpiInfo) => {
        setState({...state, kpiInfo})
    }

    const kpiProps: KpiProps = {...state, setKpiInfo, setKpiState: setState}

    const stepConfigurations = [
        {
            "icon": <FileDescription {...stepperIconProps}/>,
            title: "Define your KPI",
            description: "Hi there, Welcome to KPI management wizard. This wizard helps you to make your own KPI. As the first step of this process, please provide a name and description for your KPI. Remember we will use this name as a unique identifier in our KPI-related APIs and a good description will be very useful for whoever referencing this KPI",
            stepper: <KpiDefinitionStep {...kpiProps}/>,
            validator: new KpiDefinitionValidator()
        },
        {
            "icon": <Filter {...stepperIconProps}/>,
            title: "Target Configuration",
            description: "The KPI can vary from user to user. These filters allow you to set up the target users based on your needs. ",
            stepper: <KpiTargetFilterConfigurationStep {...kpiProps}/>
        },
        {
            "icon": <Database {...stepperIconProps}/>,
            title: "Query Configuration",
            description: "A perfect KPI can be a combination of different data sets. We need your help to configure these data set extraction queries. Please note that for better performance please prepare the necessary data points through our aggregation platform.",
            stepper: <SqlQueryConfigurationStep  {...kpiProps}/>,
            validator: new SqlQueryConfigurationValidator()
        },
        {
            "icon": <Code {...stepperIconProps}/>,
            title: "Script Configuration",
            description: "Sometimes extracting a KPI from a different data set can be complex. To find out those KPIs, we may need to apply some algorithms to the data set. This optional scripting will allow you to apply your algorithms to the extracted data set.",
            stepper: <ScriptConfigurationStep  {...kpiProps}/>,
            validator: new ScriptConfigurationValidator()
        },
        {
            "icon": <Clock {...stepperIconProps}/>,
            title: "Scheduler Configuration",
            description: "Great! You have successfully created the KPI Configuration. Let's schedule it now:) We are supporting two types of scheduling. If this KPI depends on any pre-aggregation task, then please choose those aggregations as dependent tasks so that Sellina can immediately run this KPI task whenever those dependent tasks execute successfully. If you want to run this at a specific time, then schedule it through our scheduler UI.",
            stepper: <SchedulerConfigurationStep  {...kpiProps}/>,
            validator: new SchedulerConfigurationValidator()

        },
        {
            "icon": <ListCheck {...stepperIconProps}/>,
            title: "Review your configuration",
            description: "One last step. We need you to carefully evaluate the configurations you have choose. You can always go back and change those configuration based on your needs.",
            stepper: <ReviewStep  {...kpiProps}/>
        },
    ]

    const getSteps = () => {
        return stepConfigurations.map(item => {
            return (
                <Stepper.Step label={matches && item.title} key={item.description} icon={item.icon} >
                    <StepperContent title={!matches && item.title} description={!matches ? item.description : ""}>
                        {item.stepper}
                    </StepperContent>
                </Stepper.Step>
            )
        })
    }

     // Allow the user to freely go back to visited steps
    const shouldAllowSelectStep = (step: number) => {
        if(highestStepVisited >= step && active !== step){
            setHighestStepVisited(step);
            setActive(step);
        }
    }

    async function submitKpi() {
        setPushing("started")
        try {
            const kpiInfo = await updateKpiInfo(state.kpiInfo, authContext);
            console.log(kpiInfo)
            setPushing("completed")
        } catch (er) {
            setPushing("completed")
        }
    }

    if (pushing === 'started') {
        return <LoaderCard>
            <h1>Saving your KPI configurations</h1>
            <Loader/>
        </LoaderCard>;
    }

    if (pushing === 'completed') {
        return <LoaderCard>
            <h3>Your configuration has been saved successfully</h3>
            <Button variant={"outline"} color="teal" onClick={() => navigate("/")}>Take me to the HomePage!</Button>
        </LoaderCard>;
    }


    return (
        <>
            <div className={"kpi-setup-wrapper"}>
                <div className={"kpi-setup-stepper full-width-height"}>

                    <Stepper className={"full-width-height"} size={"xl"} active={active} breakpoint={"md"}
                             onStepClick={shouldAllowSelectStep} color={"teal"} >¯
                        {getSteps()}
                    </Stepper>
                </div>
                <div className={"kpi-setup-buttons"}>
                    <Button color="teal" variant="default" onClick={prevStep}>Back</Button>
                    {active !== 5 ? <Button color="teal" onClick={nextStep}>Next step</Button> :
                        <Button color="teal" onClick={ () => setSaveKpiConfirmationModal(true)}>Submit</Button>
                    }
                </div>
            </div>

            <ConfirmationPopUp message="Are you sure you want to save this KPI?"
                openConfirmModal={openSaveKpiConfirmationModal}
                setOpenConfirmModal={setSaveKpiConfirmationModal}
                successMethod={() => submitKpi()}
            />

        </>
    );
}